import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductCategory } from '@/mocks/products';

interface ProductShowcaseProps {
  categories: ProductCategory[];
}

export default function ProductShowcase({ categories }: ProductShowcaseProps) {
  const { t } = useTranslation();
  const scrollRefs = useRef<(HTMLDivElement | null)[]>([]);

  const scroll = (index: number, direction: 'left' | 'right') => {
    const el = scrollRefs.current[index];
    if (!el) return;
    const scrollAmount = 280;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  return (
    <section className="py-16 md:py-20 bg-gray-50">
      <div className="w-full px-4 sm:px-6 lg:px-10 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{t('products.title')}</h2>
          <p className="text-gray-600 text-sm md:text-base max-w-xl mx-auto">
            {t('products.description')}
          </p>
        </div>

        {categories.map((category, idx) => (
          <div key={category.name} className="mb-14 last:mb-0">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg md:text-xl font-bold text-gray-900">{category.name}</h3>
              <a
                href={category.url}
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                {t('products.viewAll')}
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="relative group">
              <button
                onClick={() => scroll(idx, 'left')}
                className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer"
                aria-label="Scroll left"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>

              <div
                ref={(el) => { scrollRefs.current[idx] = el; }}
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scroll-smooth scrollbar-hide"
              >
                {category.products.map((product) => (
                  <div
                    key={product.id}
                    className="flex-shrink-0 w-[200px] md:w-[240px] snap-start bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <a
                      href={product.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block"
                    >
                      <div className="aspect-square overflow-hidden bg-gray-100">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    </a>
                    <div className="p-3">
                      <a
                        href={product.link}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block"
                      >
                        <h4 className="text-xs font-medium text-gray-900 line-clamp-2 mb-1 leading-snug min-h-[2.5rem]">
                          {product.name}
                        </h4>
                        <p className="text-sm font-semibold text-emerald-600 mb-2">{product.price}</p>
                      </a>
                      <a
                        href={product.link}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block w-full text-center py-2 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors"
                      >
                        {t('products.buyNow')}
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => scroll(idx, 'right')}
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer"
                aria-label="Scroll right"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}